// src/auth/components/AuthWidget.jsx
import React, { useState, useEffect } from "react";
import { Form, Button, Alert, Card } from "react-bootstrap";
import { authClient } from "../api/authClient";
import { useAuth } from "../context/AuthProvider";

const MODES = {
  SIGN_IN: "SIGN_IN",
  REGISTER: "REGISTER",
  VERIFY_CODE: "VERIFY_CODE",
  FORGOT_PASSWORD: "FORGOT_PASSWORD",
  RESET_PASSWORD: "RESET_PASSWORD",
  PROFILE: "PROFILE",
};

export default function AuthWidget({
  onAuthenticated,
  initialMode = MODES.SIGN_IN,
  verificationToken,
  resetToken,
}) {
  const { user, login, setUser } = useAuth(); // setUser used in handleSignOut

  const [mode, setMode] = useState(initialMode);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Form States
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [regUsername, setRegUsername] = useState("");
  const [regEmail, setRegEmail] = useState("");
  const [regConfirmEmail, setRegConfirmEmail] = useState("");
  const [regPassword, setRegPassword] = useState("");
  const [verifyCode, setVerifyCode] = useState(verificationToken || "");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  useEffect(() => {
    // if already logged in and user lands on Sign In, go to Profile
    if (user && mode === MODES.SIGN_IN) {
      setMode(MODES.PROFILE);
    }
  }, [user, mode]);

  const clearMessages = () => {
    setError("");
    setSuccess("");
  };

  // --- HANDLERS ---

  const handleSignIn = async (e) => {
    e.preventDefault();
    clearMessages();
    setIsLoading(true);
    try {
      const result = await login({ email, password });
      if (!result?.ok) {
        setError(result?.message || "Sign-in failed.");
      } else {
        if (onAuthenticated) {
          onAuthenticated({ redirectPath: "/auth/handoff", user: result.user });
        } else {
          setMode(MODES.PROFILE);
        }
      }
    } catch (err) {
      setError("Network error during handshake.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    clearMessages();
    if (regEmail !== regConfirmEmail) {
      setError("Emails do not match.");
      return;
    }
    setIsLoading(true);
    try {
      // Aligned with RegisterCoreRequest
      const result = await authClient.register({
        userName: regUsername.trim(),
        email: regEmail.trim(),
        password: regPassword,
      });
      if (!result?.ok) setError(result?.message || "Registration failed.");
      else {
        setSuccess("Registration successful. Please verify your email.");
        setMode(MODES.VERIFY_CODE);
      }
    } catch {
      setError("Registration service error.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    clearMessages();

    if (!resetToken) {
      setError("Reset token missing.");
      return;
    }

    // ✅ Hard validation: no null/empty/whitespace, must match, min length
    if (!newPassword || !confirmPassword) {
      setError("Both password fields are required.");
      return;
    }

    const trimmed = newPassword.trim();
    if (trimmed.length === 0) {
      setError("Password cannot be empty or whitespace.");
      return;
    }

    if (trimmed.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }

    if (trimmed !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setIsLoading(true);
    try {
      const result = await authClient.resetPassword({
        token: resetToken,
        newPasswordHash: trimmed,
      });

      if (result.ok || result.Success || result.success) {
        setSuccess("Password updated! Redirecting...");
        // clear fields so we never re-submit stale/empty values
        setNewPassword("");
        setConfirmPassword("");
        setTimeout(() => setMode(MODES.SIGN_IN), 1500);
      } else {
        setError(result.Message || result.message || "Reset failed.");
      }
    } catch (err) {
      setError("Server error.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyEmail = async (e) => {
    e.preventDefault();
    clearMessages();
    setIsLoading(true);
    try {
      // Aligned with VerifyEmailRequest { verificationToken }
      const result = await authClient.verifyEmail({ verificationToken: verifyCode });
      if (result.ok) {
        setSuccess("Email verified successfully.");
        setMode(MODES.SIGN_IN);
      } else setError(result.message);
    } catch {
      setError("Verification service error.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    clearMessages();
    setIsLoading(true);
    try {
      const result = await authClient.forgotPassword({ email });
      if (result.ok) setSuccess("If an account exists, a reset link has been sent.");
      else setError(result.message || "Request failed.");
    } catch {
      setError("Service error.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignOut = async () => {
    setIsLoading(true);
    try {
      await authClient.signOut();
      setUser(null);
      setMode(MODES.SIGN_IN);
    } catch {
      setUser(null);
      setMode(MODES.SIGN_IN);
    } finally {
      setIsLoading(false);
    }
  };

  // --- RENDER VIEWS ---

  const renderSignIn = () => (
    <Form onSubmit={handleSignIn}>
      <Form.Group className="mb-3">
        <Form.Label className="small">Email</Form.Label>
        <Form.Control
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          autoComplete="username"
        />
      </Form.Group>
      <Form.Group className="mb-3">
        <Form.Label className="small">Password</Form.Label>
        <Form.Control
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          autoComplete="current-password"
        />
      </Form.Group>
      <Button type="submit" disabled={isLoading} className="w-100">
        Sign In
      </Button>
      <div className="mt-3 d-flex justify-content-between small">
        <Button
          variant="link"
          className="p-0 text-decoration-none"
          onClick={() => {
            clearMessages();
            setMode(MODES.REGISTER);
          }}
        >
          Register
        </Button>
        <Button
          variant="link"
          className="p-0 text-decoration-none"
          onClick={() => {
            clearMessages();
            setMode(MODES.FORGOT_PASSWORD);
          }}
        >
          Forgot?
        </Button>
      </div>
    </Form>
  );

  const renderRegister = () => (
    <Form onSubmit={handleRegister}>
      <Form.Group className="mb-2">
        <Form.Label className="small fw-bold">Username</Form.Label>
        <Form.Control value={regUsername} onChange={(e) => setRegUsername(e.target.value)} required />
      </Form.Group>
      <Form.Group className="mb-2">
        <Form.Label className="small">Email</Form.Label>
        <Form.Control
          type="email"
          value={regEmail}
          onChange={(e) => setRegEmail(e.target.value)}
          required
        />
      </Form.Group>
      <Form.Group className="mb-2">
        <Form.Label className="small">Confirm Email</Form.Label>
        <Form.Control
          type="email"
          value={regConfirmEmail}
          onChange={(e) => setRegConfirmEmail(e.target.value)}
          required
        />
      </Form.Group>
      <Form.Group className="mb-3">
        <Form.Label className="small">Password</Form.Label>
        <Form.Control
          type="password"
          value={regPassword}
          onChange={(e) => setRegPassword(e.target.value)}
          required
          autoComplete="new-password"
        />
      </Form.Group>
      <Button type="submit" disabled={isLoading} className="w-100">
        Create Account
      </Button>
      <Button
        variant="link"
        onClick={() => {
          clearMessages();
          setMode(MODES.SIGN_IN);
        }}
        className="w-100 small mt-2"
      >
        Back to Sign In
      </Button>
    </Form>
  );

  const renderForgotPassword = () => (
    <Form onSubmit={handleForgotPassword}>
      <p className="small text-muted mb-3">Enter your email to receive a reset link.</p>
      <Form.Group className="mb-3">
        <Form.Control
          type="email"
          placeholder="Email Address"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          autoComplete="username"
        />
      </Form.Group>
      <Button type="submit" disabled={isLoading} className="w-100">
        Send Reset Link
      </Button>
      <Button
        variant="link"
        onClick={() => {
          clearMessages();
          setMode(MODES.SIGN_IN);
        }}
        className="w-100 small mt-2"
      >
        Back to Sign In
      </Button>
    </Form>
  );

  const renderResetPassword = () => (
    <Form onSubmit={handleResetPassword}>
      {/* Hidden username field (helps Chrome + accessibility warning) */}
      <Form.Control type="text" value={email} readOnly autoComplete="username" style={{ display: "none" }} />

      <Form.Group className="mb-3">
        <Form.Label className="small">New Password</Form.Label>
        <Form.Control
          type="password"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          autoComplete="new-password"
          required
        />
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label className="small">Confirm Password</Form.Label>
        <Form.Control
          type="password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          autoComplete="new-password"
          required
        />
      </Form.Group>

      <Button type="submit" disabled={isLoading} className="w-100">
        Update Password
      </Button>

      <Button
        variant="link"
        onClick={() => {
          clearMessages();
          setMode(MODES.SIGN_IN);
        }}
        className="w-100 small mt-2"
      >
        Back to Sign In
      </Button>
    </Form>
  );

  const renderVerifyCode = () => (
    <Form onSubmit={handleVerifyEmail}>
      <Form.Group className="mb-3">
        <Form.Label className="small">Verification Code</Form.Label>
        <Form.Control value={verifyCode} onChange={(e) => setVerifyCode(e.target.value)} required />
      </Form.Group>
      <Button type="submit" disabled={isLoading} className="w-100">
        Verify Email
      </Button>

      <Button
        variant="link"
        onClick={() => {
          clearMessages();
          setMode(MODES.SIGN_IN);
        }}
        className="w-100 small mt-2"
      >
        Back to Sign In
      </Button>
    </Form>
  );

  const renderProfile = () => (
    <div className="text-center">
      <p className="mb-1 text-muted small">Logged in as:</p>
      <h5 className="fw-bold mb-4">{user?.userName || "User"}</h5>
      <Button variant="outline-danger" className="w-100" onClick={handleSignOut} disabled={isLoading}>
        Sign Out
      </Button>
    </div>
  );

  // --- DYNAMIC SELECTION ---
  let title = "Sign In",
    body = renderSignIn();

  if (user && mode === MODES.PROFILE) {
    title = "Account Settings";
    body = renderProfile();
  } else if (mode === MODES.REGISTER) {
    title = "Create Account";
    body = renderRegister();
  } else if (mode === MODES.RESET_PASSWORD) {
    title = "Set New Password";
    body = renderResetPassword();
  } else if (mode === MODES.VERIFY_CODE) {
    title = "Verify Your Account";
    body = renderVerifyCode();
  } else if (mode === MODES.FORGOT_PASSWORD) {
    title = "Forgot Password";
    body = renderForgotPassword();
  }

  return (
    <Card className="border-0 shadow-sm" style={{ borderRadius: "12px" }}>
      <Card.Body className="p-4">
        <h2 className="mb-4 h5 fw-bold text-center">{title}</h2>
        {error && <Alert variant="danger" className="py-2 small text-center">{error}</Alert>}
        {success && <Alert variant="success" className="py-2 small text-center">{success}</Alert>}
        {body}
      </Card.Body>
    </Card>
  );
}
